/**
 * Creates a slider for picking intervals or numbers.
 *
 * A slider can be created with one or two draggable thumbs indicating the values.
 *
 * A slider with one thumb is created with
 *
 *   new Slider(startInputElement)
 *
 * A slider with two thumbs is created with
 *
 *   new Slider(startInputElement, endInputElement)
 *
 * The startInputElement must have a `min` attribute specified. If the slider has one thumb,
 * the startInputElement must also have a `max` attribute specified. Otherwise, the `max`
 * attribute must be specified on the endInputElement.
 *
 * The `step` attribute may be specified on the startInputElement, in which case values
 * will be set in steps.
 *
 * The `value` attribute may be specified on both elements, defining the initial value. If it's
 * not specified, the initial values will be set to the minimum and maximum values.
 *
 * The slider's interval will be the closed interval
 *
 *   [startInputElement.min, endInputElement.max]
 *
 * If the `data-unbounded-end` attribute is specified on one of the elements, the corresponding
 * end will be unbounded which means that the value of the element, upon reaching its maximum or
 * minimum value, will be set to the empty string.
 */

/*
TODO:
Handle division by zero
getKey()
*/
function Slider(startInputEle, endInputEle, options) {
    if (!startInputEle) {
        throw Error('A start input element has to be specified.');
    }

    options = options || {};
    this._sliderEle = options.container;
    this._format =
        options.formatter ||
        function(value) {
            return value;
        };
    this._suffixformatter =
        options.suffixformatter ||
        function(value, suffix) {
            return suffix;
        };

    const defaultDistribution = {
        fromBaseToConverted(value /* , minValue */) {
            return value;
        },
        fromConvertedToBase(value /* , minValue */) {
            return value;
        },
        roundBaseValue: function(baseValue) {
            return Math.round(baseValue / this._step) * this._step;
        }.bind(this),
        nextBaseValue: function(baseValue, nextSteps) {
            return baseValue + this._step * nextSteps;
        }.bind(this),
        previousBaseValue: function(baseValue, previousSteps) {
            return baseValue - this._step * previousSteps;
        }.bind(this),
    };

    // distribution defines how values will be distribution in the slider.
    // One tick does not have to give you the same step value, and high values can be given more space on the slider than low values.
    this._distribution = options.distribution || defaultDistribution;

    this._output = options.output || this._setOutputValue;
    this._snap = Boolean(options.snap);
    this._isRangeSlider = Boolean(endInputEle);
    this._activeThumb = null;
    this._activeDrag = false;
    this._pageX = 0;
    this._dragStartX = 0;
    this._rafId = null;
    this._keyBuffer = '';
    this._handleMouseDown = this._handleMouseDown.bind(this);
    this._handleMouseUp = this._handleMouseUp.bind(this);
    this._handleMouseMove = this._handleMouseMove.bind(this);
    this._handleTouchStart = this._handleTouchStart.bind(this);
    this._handleTouchEnd = this._handleTouchEnd.bind(this);
    this._handleTouchMove = this._handleTouchMove.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._handleKeyPress = this._handleKeyPress.bind(this);
    this._handleBlur = this._handleBlur.bind(this);
    this._updateThumbPosition = this._updateThumbPosition.bind(this);

    if (this._sliderEle) {
        const thumbs = this._sliderEle.querySelectorAll('.finn-slider-thumb');
        this._startThumbEle = thumbs[0];
        this._endThumbEle = thumbs[1] || null;
        this._trackInnerEle = this._sliderEle.querySelector(
            '.finn-slider-track-inner'
        );
        this._outputEle = this._sliderEle.querySelector('.finn-slider-output');
    } else {
        this._createElements();
    }

    if (this._isRangeSlider) {
        this._setupRangeSlider(startInputEle, endInputEle);
    } else {
        this._setupSlider(startInputEle);
    }

    this._output(this.startValue, this.endValue);

    if (!this._sliderEle.parentNode) {
        startInputEle.parentNode.insertBefore(this._sliderEle, startInputEle);
    }

    this.layout();

    window.addEventListener('resize', this.layout.bind(this));
}

Slider.prototype = {
    /**
     * Sets the start value of the slider.
     */
    setStartValue(value) {
        if (typeof value != 'number') {
            throw new TypeError('Value must be a number.');
        }
        if (value !== this.startValue) {
            this._setStartValue(value);
            this._fireSyntheticEvent('change', 'start');
        }
    },

    /**
     * Sets the end value of the slider.
     */
    setEndValue(value) {
        if (typeof value != 'number') {
            throw new TypeError('Value must be a number.');
        }
        this._setEndValue(value);
        this._setEndThumbPosition(this._scaleValueToWidth(this.endValue));
        this._fireSyntheticEvent('change', 'end');
    },

    getOutputValue(start, end) {
        const displayedStart = this._fallbackIfInfinity(start, this.minValue);
        const displayedEnd = this._fallbackIfInfinity(end, this.maxValue);
        const formattedStartValue = this._format(this._toFixed(displayedStart));
        const formattedEndValue = this._format(this._toFixed(displayedEnd));
        const suffix = this._suffixformatter(
            this._toFixed(displayedStart),
            this._outputSuffix
        );
        let startLabel = this._outputLabel;
        let endLabel = this._outputLabel;
        let value;

        if (
            displayedStart === this.minValue &&
            this._startUnboundedOutputLabel
        ) {
            startLabel = this._startUnboundedOutputLabel;
        }

        if (this._isRangeSlider) {
            if (
                displayedEnd === this.maxValue &&
                this._endUnboundedOutputLabel
            ) {
                endLabel = this._endUnboundedOutputLabel;
            }

            if (displayedStart === displayedEnd) {
                const label =
                    displayedStart === this.minValue ? startLabel : endLabel;
                value = label.replace('{value}', formattedStartValue);
            } else if (displayedStart > this.maxValue && end === Infinity) {
                value = endLabel.replace('{value}', formattedStartValue);
            } else if (displayedEnd < this.minValue && start === -Infinity) {
                value = startLabel.replace('{value}', formattedEndValue);
            } else {
                value = `${startLabel.replace(
                    '{value}',
                    formattedStartValue
                )} – ${endLabel.replace('{value}', formattedEndValue)}`;
            }
        } else {
            value = startLabel.replace('{value}', formattedStartValue);
        }
        return `${value} ${suffix}`;
    },

    /**
     * Sets the size of the slider. In cases where the slider element has
     * display set to none while the slider initializes, this has to be run
     * after the display is set to something else.
     */
    layout() {
        window.requestAnimationFrame(() => {
            this._sliderWidth = this._sliderEle.offsetWidth;
            this._sliderLeft = this._sliderEle.offsetLeft;
            this._thumbWidth = this._startThumbEle.offsetWidth;
            this._endThumbLeft = this._sliderWidth - this._thumbWidth;
            this._setStartThumbPosition(
                this._scaleValueToWidth(this.startValue)
            );
            if (this._isRangeSlider) {
                this._setEndThumbPosition(
                    this._scaleValueToWidth(this.endValue)
                );
            }
        });
    },

    // Simplistic implementation
    dispatchEvent(event) {
        this._sliderEle.dispatchEvent(event);
    },

    // Simplistic implementation
    addEventListener(type, callback, capture) {
        this._sliderEle.addEventListener(type, callback, capture || false);
    },

    // Simplistic implementation
    removeEventListener(type, callback, capture) {
        this._sliderEle.removeEventListener(type, callback, capture || false);
    },

    _setupSlider(startInputEle) {
        const step = (startInputEle.getAttribute('step') || '').trim();
        const isFraction = step.indexOf('.') !== -1;
        this.minValue = Number(startInputEle.getAttribute('min'));
        this.maxValue = Number(startInputEle.getAttribute('max'));
        this.startValue =
            startInputEle.value === ''
                ? -Infinity
                : Number(startInputEle.value);
        this.endValue = this.maxValue;
        this._startInputEle = startInputEle;
        this._step = Number(step) || 1;
        this._fractionDigits = isFraction
            ? step.slice(step.indexOf('.') + 1).length
            : 0;
        this._outputLabel =
            startInputEle.getAttribute('data-output') || '{value}';
        this._outputSuffix =
            startInputEle.getAttribute('data-output-suffix') || '';
        const unboundedAttr = startInputEle.getAttribute('data-unbounded-end');
        this._isLeftUnboundedInterval =
            unboundedAttr != null && unboundedAttr !== 'false';
        this._startUnboundedOutputLabel =
            startInputEle.getAttribute('data-unbounded-output') || '{value}';

        if (!this._startThumbEle.hasAttribute('tabindex')) {
            this._startThumbEle.tabIndex = 0;
        }

        this._startThumbEle.setAttribute('role', 'slider');
        this._startThumbEle.setAttribute('aria-valuemin', this.minValue);
        this._startThumbEle.setAttribute('aria-valuemax', this.maxValue);
        const ariaValue = this._fallbackIfInfinity(
            this.startValue,
            this.minValue
        );
        this._startThumbEle.setAttribute(
            'aria-valuenow',
            this._toFixed(ariaValue)
        );
        this._startThumbEle.setAttribute(
            'aria-valuetext',
            this._toFixed(ariaValue)
        );
        if (!this._startThumbEle.hasAttribute('aria-label')) {
            this._startThumbEle.setAttribute('aria-label', 'Start');
        }

        this._trackInnerEle.style.right = '0';

        this._setStartInputValue(this.startValue);

        this._startInputEle.addEventListener('input', event => {
            this.setStartValue(Number(event.target.value));
        });

        this._startThumbEle.addEventListener(
            'mousedown',
            this._handleMouseDown
        );
        this._startThumbEle.addEventListener(
            'touchstart',
            this._handleTouchStart
        );
        this._startThumbEle.addEventListener('keydown', this._handleKeyDown);
        this._startThumbEle.addEventListener('keypress', this._handleKeyPress);
        this._startThumbEle.addEventListener('blur', this._handleBlur);
    },

    _setupRangeSlider(startInputEle, endInputEle) {
        startInputEle.setAttribute('max', endInputEle.getAttribute('max'));
        endInputEle.setAttribute('min', startInputEle.getAttribute('min'));
        this._setupSlider(startInputEle);
        this.maxValue = Number(endInputEle.getAttribute('max'));
        endInputEle.step = this._step;
        this.endValue =
            endInputEle.value === '' ? Infinity : Number(endInputEle.value);
        this._endInputEle = endInputEle;
        const unboundedAttr = endInputEle.getAttribute('data-unbounded-end');
        this._isRightUnboundedInterval =
            unboundedAttr != null && unboundedAttr !== 'false';
        this._endUnboundedOutputLabel =
            endInputEle.getAttribute('data-unbounded-output') || '{value}';

        if (!this._endThumbEle.hasAttribute('tabindex')) {
            this._endThumbEle.tabIndex = 0;
        }

        this._endThumbEle.setAttribute('role', 'slider');
        this._endThumbEle.setAttribute('aria-valuemin', this.minValue);
        this._endThumbEle.setAttribute('aria-valuemax', this.maxValue);
        const ariaValue = this._fallbackIfInfinity(
            this.endValue,
            this.maxValue
        );
        this._endThumbEle.setAttribute(
            'aria-valuenow',
            this._toFixed(ariaValue)
        );
        this._endThumbEle.setAttribute(
            'aria-valuetext',
            this._toFixed(ariaValue)
        );
        if (!this._endThumbEle.hasAttribute('aria-label')) {
            this._endThumbEle.setAttribute('aria-label', 'End');
        }

        this._endThumbEle.style.removeProperty('right');

        this._setEndInputValue(this.endValue);

        this._endInputEle.addEventListener('input', event => {
            this.setEndValue(Number(event.target.value));
        });

        this._endThumbEle.addEventListener('mousedown', this._handleMouseDown);
        this._endThumbEle.addEventListener(
            'touchstart',
            this._handleTouchStart
        );
        this._endThumbEle.addEventListener('keydown', this._handleKeyDown);
        this._endThumbEle.addEventListener('keypress', this._handleKeyPress);
        this._endThumbEle.addEventListener('blur', this._handleBlur);
    },

    _createElements() {
        this._sliderEle = document.createElement('div');
        this._sliderEle.className = 'finn-slider';
        const trackEle = document.createElement('div');
        trackEle.className = 'finn-slider-track';
        this._trackInnerEle = document.createElement('div');
        this._trackInnerEle.className = 'finn-slider-track-inner';
        this._startThumbEle = document.createElement('div');
        this._startThumbEle.className = 'finn-slider-thumb';
        this._outputEle = document.createElement('output');
        this._outputEle.className = 'finn-slider-output';
        this._sliderEle.appendChild(trackEle);
        this._sliderEle.appendChild(this._trackInnerEle);
        this._sliderEle.appendChild(this._startThumbEle);
        if (this._isRangeSlider) {
            this._endThumbEle = document.createElement('div');
            this._endThumbEle.className = 'finn-slider-thumb';
            this._sliderEle.appendChild(this._endThumbEle);
        }
        this._sliderEle.insertBefore(
            this._outputEle,
            this._sliderEle.firstChild
        );
    },

    _setClampedStartValue(value) {
        const currentEndValue = Math.min(this.endValue, this.maxValue);
        this._setStartValue(this._clamp(value, this.minValue, currentEndValue));
    },

    _setClampedEndValue(value) {
        const currentStartValue = Math.max(this.startValue, this.minValue);
        this._setEndValue(this._clamp(value, currentStartValue, this.maxValue));
    },

    _setStartValue(value) {
        if (this.startValue !== value) {
            if (
                this._isRangeSlider &&
                value > this.endValue &&
                this.endValue !== Infinity
            ) {
                this.setEndValue(value);
            }

            this.startValue = value;
            this._setStartThumbPosition(this._scaleValueToWidth(value));
            this._output(value, this.endValue);
            this._setStartInputValue(value);
            const ariaValue = this._fallbackIfInfinity(value, this.endValue);
            this._startThumbEle.setAttribute(
                'aria-valuenow',
                this._toFixed(ariaValue)
            );
            this._startThumbEle.setAttribute(
                'aria-valuetext',
                this._toFixed(ariaValue)
            );
        }
    },

    _setEndValue(value) {
        if (this.endValue !== value) {
            if (value < this.startValue && this.startValue !== -Infinity) {
                this.setStartValue(value);
            }

            this.endValue = value;
            this._setEndThumbPosition(this._scaleValueToWidth(value));
            this._output(this.startValue, value);
            this._setEndInputValue(value);
            const ariaValue = this._fallbackIfInfinity(value, this.startValue);
            this._endThumbEle.setAttribute(
                'aria-valuenow',
                this._toFixed(ariaValue)
            );
            this._endThumbEle.setAttribute(
                'aria-valuetext',
                this._toFixed(ariaValue)
            );
        }
    },

    _setStartInputValue(value) {
        let outputValue;
        if (
            (value === this.minValue && this._isLeftUnboundedInterval) ||
            value === -Infinity
        ) {
            outputValue = '';
        } else {
            outputValue = this._toFixed(value);
        }

        this._startInputEle.value = outputValue;
        this._fireSyntheticEvent('input', 'start');
    },

    _setEndInputValue(value) {
        let outputValue;
        if (
            (value === this.maxValue && this._isRightUnboundedInterval) ||
            value === Infinity
        ) {
            outputValue = '';
        } else {
            outputValue = this._toFixed(value);
        }

        this._endInputEle.value = outputValue;
        this._fireSyntheticEvent('input', 'end');
    },

    _setOutputValue(start, end) {
        this._outputEle.textContent = this.getOutputValue(start, end);
    },

    _setStartThumbPosition(value) {
        value = this._clamp(value, 0, this._endThumbLeft);
        this._startThumbLeft = value;
        this._startThumbEle.style.left = `${value.toFixed(1)}px`;
        this._trackInnerEle.style.left = `${(value +
            this._thumbWidth / 2
        ).toFixed(1)}px`;
    },

    _setEndThumbPosition(value) {
        value = this._clamp(
            value,
            this._startThumbLeft,
            this._sliderWidth - this._thumbWidth
        );
        this._endThumbLeft = value;
        this._endThumbEle.style.left = `${value.toFixed(1)}px`;
        this._trackInnerEle.style.right = `${(this._sliderWidth -
            value -
            this._thumbWidth / 2
        ).toFixed(1)}px`;
    },

    _updateThumbPosition() {
        if (this._rafId) {
            return;
        }

        this._rafId = window.requestAnimationFrame(() => this._render());
    },

    _render() {
        // TODO: clean this up
        const thumbLeft = this._pageX - this._sliderLeft - this._dragStartX;
        const thumbPercent = thumbLeft / (this._sliderWidth - this._thumbWidth);
        const convertedMinValue = this._distribution.fromBaseToConverted(
            this.minValue,
            this.minValue
        );
        const convertedMaxValue = this._distribution.fromBaseToConverted(
            this.maxValue,
            this.minValue
        );
        const convertedValue =
            convertedMinValue +
            (convertedMaxValue - convertedMinValue) * Math.max(thumbPercent, 0);
        const value = this._distribution.fromConvertedToBase(
            convertedValue,
            this.minValue
        );
        const stepValue = this._distribution.roundBaseValue(value);

        if (this._activeThumb === this._startThumbEle) {
            this._setClampedStartValue(stepValue);
            if (!this._snap) {
                this._setStartThumbPosition(thumbLeft);
            }
        } else if (this._activeThumb === this._endThumbEle) {
            this._setClampedEndValue(stepValue);
            if (!this._snap) {
                this._setEndThumbPosition(thumbLeft);
            }
        }
        this._rafId = null;
    },

    _handleMouseDown(event) {
        event.stopPropagation();
        event.preventDefault();

        if (event.button !== 0) {
            return;
        }

        const target = event.target;
        // Could be event.offsetX, but not all browsers support
        this._dragStartX = event.pageX - target.offsetLeft - this._sliderLeft;
        this._pageX = event.pageX;
        this._activeDrag = true;

        // TODO: within some threshold?
        if (
            this._isRangeSlider &&
            this._startThumbLeft === this._endThumbLeft
        ) {
            this._observeMouseMoveDirection(target, event.pageX);
        }

        this._addMouseListeners(target);
    },

    _handleMouseUp(event) {
        event.stopPropagation();

        // TODO: only update if some value has changed
        this._fireSyntheticEvent('change', this._getActiveThumbLabel());
        this._removeTemporaryStyles(this._activeThumb);
        this._activeDrag = false;
        document.removeEventListener('mouseup', this._handleMouseUp);
        document.removeEventListener('mousemove', this._handleMouseMove);
    },

    _handleMouseMove(event) {
        event.stopPropagation();
        event.preventDefault();

        this._pageX = event.pageX;
        this._updateThumbPosition();
    },

    _handleTouchStart(event) {
        event.stopPropagation();
        event.preventDefault();

        // Prevent more than one touch on the same slider
        if (this._activeDrag) {
            return;
        }

        const target = event.target;
        const touch = event.targetTouches[0];
        this._dragStartX = touch.pageX - target.offsetLeft - this._sliderLeft;
        this._pageX = touch.pageX;
        this._activeDrag = true;

        // TODO: within some threshold?
        if (
            this._isRangeSlider &&
            this._startThumbLeft === this._endThumbLeft
        ) {
            this._observeTouchMoveDirection(target, touch.pageX);
        }

        // We want to skip the next mousedown handler at this point, we'll
        // add it back later.
        target.removeEventListener('mousedown', this._handleMouseDown);

        this._addTouchListeners(target);
    },

    _handleTouchEnd(event) {
        event.stopPropagation();

        // TODO: only update if some value has changed
        this._fireSyntheticEvent('change', this._getActiveThumbLabel());
        this._removeTemporaryStyles(this._activeThumb);
        this._activeDrag = false;
        const target = event.target;
        target.addEventListener('mousedown', this._handleMouseDown);
        target.removeEventListener('touchend', this._handleTouchEnd);
        target.removeEventListener('touchcancel', this._handleTouchEnd);
        target.removeEventListener('touchmove', this._handleTouchMove);
    },

    _handleTouchMove(event) {
        event.stopPropagation();
        event.preventDefault();

        const touches = event.targetTouches;
        const touch = touches[0];
        if (!touch) {
            // TODO: can this ever happen?
            return;
        }

        this._pageX = touch.pageX;
        this._updateThumbPosition();
    },

    _handleKeyDown(event) {
        event.stopPropagation();

        const isStartThumb = event.target === this._startThumbEle;
        let value = isStartThumb
            ? this._fallbackIfInfinity(this.startValue, this.minValue)
            : this._fallbackIfInfinity(this.endValue, this.maxValue);

        if (event.keyCode === 8 /* Backspace */) {
            this._keyBuffer = this._keyBuffer.slice(0, -1);
            this._outputKeyBuffer(isStartThumb ? 'start' : 'end');
            event.preventDefault();
            return;
        } else {
            switch (event.key || event.keyIdentifier) {
                case 'ArrowLeft':
                case 'ArrowDown':
                case 'Left':
                case 'Down': {
                    const previousValue = this._distribution.previousBaseValue(
                        value,
                        1
                    );
                    value = Math.max(previousValue, this.minValue);
                    break;
                }
                case 'ArrowRight':
                case 'ArrowUp':
                case 'Right':
                case 'Up': {
                    const nextValue = this._distribution.nextBaseValue(
                        value,
                        1
                    );
                    value = Math.min(nextValue, this.maxValue);
                    break;
                }
                case 'PageDown': {
                    const previousValue = this._distribution.previousBaseValue(
                        value,
                        10
                    );
                    value = Math.max(previousValue, this.minValue);
                    break;
                }
                case 'PageUp': {
                    const nextValue = this._distribution.nextBaseValue(
                        value,
                        10
                    );
                    value = Math.min(nextValue, this.maxValue);
                    break;
                }
                case 'Home':
                    value = this.minValue;
                    break;

                case 'End':
                    value = this.maxValue;
                    break;

                default:
                    return;
            }
        }

        if (isStartThumb) {
            this.setStartValue(value);
        } else {
            this.setEndValue(value);
        }

        event.preventDefault();
    },

    _handleKeyPress(event) {
        // key 0 = 48, key 9 = 57
        if (event.which < 48 || event.which > 57) {
            return;
        }

        this._keyBuffer += String.fromCharCode(event.which);
        this._outputKeyBuffer(
            event.target === this._startThumbEle ? 'start' : 'end'
        );

        event.stopPropagation();
        event.preventDefault();
    },

    _outputKeyBuffer(thumb) {
        let value = Number(this._keyBuffer);

        if (thumb === 'start') {
            if (!this._keyBuffer) {
                value = this.minValue;
            }
            this._setStartValue(value);
        } else {
            if (!this._keyBuffer) {
                value = this.maxValue;
            }

            // Prevent resetting start back to a lower value when e.g.
            // start is 20 and end should be 100 and one presses "1".
            if (value < this.startValue) {
                this._output(this.startValue, value);
                return;
            }

            this._setEndValue(value);
        }
    },

    _handleBlur() {
        this.setStartValue(this.startValue);
        if (this._isRangeSlider) {
            this.setEndValue(this.endValue);
        }
        this._resetKeyBuffer();
    },

    _addMouseListeners(target) {
        this._activeThumb = target;
        this._addTemporaryStyles(target);
        document.addEventListener('mouseup', this._handleMouseUp);
        document.addEventListener('mousemove', this._handleMouseMove);
    },

    _addTouchListeners(target) {
        this._activeThumb = target;
        this._addTemporaryStyles(target);
        target.addEventListener('touchend', this._handleTouchEnd);
        target.addEventListener('touchcancel', this._handleTouchEnd);
        target.addEventListener('touchmove', this._handleTouchMove);
    },

    _observeMouseMoveDirection(target, start) {
        const handleMouseMove = function(event) {
            const actualTarget =
                event.pageX < start ? this._startThumbEle : this._endThumbEle;
            target.removeEventListener('mousemove', handleMouseMove);
            this._removeTemporaryStyles(target);
            this._addMouseListeners(actualTarget);
        }.bind(this);
        target.addEventListener('mousemove', handleMouseMove);
    },

    _observeTouchMoveDirection(target, start) {
        const handleTouchMove = function(event) {
            const actualTarget =
                event.targetTouches[0].pageX < start
                    ? this._startThumbEle
                    : this._endThumbEle;
            target.removeEventListener('touchmove', handleTouchMove);
            this._removeTemporaryStyles(target);
            this._addTouchListeners(actualTarget);
        }.bind(this);
        target.addEventListener('touchmove', handleTouchMove);
    },

    _getActiveThumbLabel() {
        return this._activeThumb === this._startThumbEle ? 'start' : 'end';
    },

    _addTemporaryStyles(target) {
        const style = target.style;
        style.setProperty('z-index', '1');
        style.setProperty('-webkit-backface-visibility', 'hidden');
        style.setProperty('backface-visibility', 'hidden');
    },

    _removeTemporaryStyles(target) {
        const style = target.style;
        style.removeProperty('z-index');
        style.removeProperty('-webkit-backface-visibility');
        style.removeProperty('backface-visibility');
    },

    _fireSyntheticEvent(type, which) {
        const event = document.createEvent('CustomEvent');
        const isStart = which === 'start';
        const details = {
            changedInput: isStart ? this._startInputEle : this._endInputEle,
            changedValue: isStart ? this.startValue : this.endValue,
        };
        event.initCustomEvent(type, true, true, details);
        this._sliderEle.dispatchEvent(event);
    },

    _toFixed(value) {
        return value.toFixed(this._fractionDigits);
    },

    _scaleValueToWidth(value) {
        const convertedValue = this._distribution.fromBaseToConverted(
            value,
            this.minValue
        );
        const convertedMinValue = this._distribution.fromBaseToConverted(
            this.minValue,
            this.minValue
        );
        const convertedMaxValue = this._distribution.fromBaseToConverted(
            this.maxValue,
            this.minValue
        );

        const valueIntervalLength = convertedMaxValue - convertedMinValue;
        return (
            (convertedValue - convertedMinValue) *
            (this._sliderWidth - this._thumbWidth) /
            valueIntervalLength
        );
    },

    _clamp(actual, min, max) {
        return Math.min(Math.max(actual, min), max);
    },

    _fallbackIfInfinity(value, fallback) {
        if (Math.abs(value) === Infinity) {
            return fallback;
        }
        return value;
    },

    _resetKeyBuffer() {
        this._keyBuffer = '';
    },
};

export default Slider;



// WEBPACK FOOTER //
// ./lib/Slider.js